// Shared live-ranking hook for the preview card and the full screen.
// Decorates raw snapshots (mock engine or API) with presentation state:
// - trend arrows vs the rank baseline captured on first load this session
// - a short background flash on rows whose win count just increased
// - LayoutAnimation reorders on native (no-op on react-native-web)

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {LayoutAnimation, Platform, UIManager} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {
  FLASH_MS,
  MEDAL,
  avatarColorForUser,
  lbColors,
} from './leaderboardTheme';
import {mockLeaderboardEngine} from './mockData';
import {USE_MOCK, fetchLeaderboard} from './leaderboardService';
import {
  LeaderboardRowVM,
  LeaderboardSnapshot,
  MeVM,
  Period,
  RankingCriteria,
} from './types';

if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const TOP_N = 20;

export function initialsForName(name?: string): string {
  if (!name) {
    return '?';
  }
  // Masked names like "xxxxx12345" → use the digits, not "XX".
  if (/^x{3,}/i.test(name)) {
    const digits = name.replace(/\D/g, '');
    return digits ? digits.slice(-2) : '#';
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const letters = name.replace(/[^A-Za-z0-9]/g, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
}

export interface LeaderboardViewData {
  rows: LeaderboardRowVM[];
  top: (limit: number) => LeaderboardRowVM[];
  me: MeVM | null;
  totalPlayers: number;
  leaderWins: number;
  // No data yet (first load of this period this session).
  isLoading: boolean;
  isError: boolean;
  // A request is in flight (initial load or a background poll).
  isFetching: boolean;
  // The shown rows are the PREVIOUS period's data while the newly-selected
  // period loads (react-query keepPreviousData) — drive a loader on tab switch.
  isSwitching: boolean;
}

interface Options {
  // Poll cadence for live mode; the mock engine pushes its own ticks.
  refetchIntervalMs?: number;
  // 'game_wins' (default, daily/weekly tabs) or 'streak_count' (Top Streaks).
  criteria?: RankingCriteria;
}

export function useLeaderboardData(
  period: Period,
  {refetchIntervalMs = 25000, criteria = 'game_wins'}: Options = {},
): LeaderboardViewData {
  const [mockTick, setMockTick] = useState(0);

  useEffect(() => {
    if (!USE_MOCK) {
      return undefined;
    }
    return mockLeaderboardEngine.subscribe(() => {
      if (Platform.OS !== 'web') {
        LayoutAnimation.configureNext(
          LayoutAnimation.create(
            600,
            LayoutAnimation.Types.easeInEaseOut,
            LayoutAnimation.Properties.opacity,
          ),
        );
      }
      setMockTick(n => n + 1);
    });
  }, []);

  const query = useQuery<LeaderboardSnapshot, Error>(
    ['gameWinsLeaderboard', period, criteria],
    () => fetchLeaderboard(period, TOP_N, criteria),
    {
      enabled: !USE_MOCK,
      refetchInterval: refetchIntervalMs,
      keepPreviousData: true,
      staleTime: 10000,
    },
  );

  const snapshot: LeaderboardSnapshot | null = useMemo(() => {
    if (USE_MOCK) {
      return mockLeaderboardEngine.snapshot(period, criteria);
    }
    return query.data ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, criteria, mockTick, query.data]);

  // Rank baseline per period, captured on the first snapshot of the session.
  const baselineRef = useRef<Partial<Record<Period, Map<number, number>>>>({});
  // Previous win counts, to detect which rows just won (flash).
  const prevWinsRef = useRef<Partial<Record<Period, Map<number, number>>>>({});
  const flashRef = useRef<Map<number, number>>(new Map());
  // Forces one re-render after FLASH_MS so highlights fade even when the next
  // data refresh is far away (live mode polls slower than the flash decay).
  const [flashTick, setFlashTick] = useState(0);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
    },
    [],
  );

  // The caller's own baseline rank, tracked separately because they may sit
  // outside the returned top list.
  const meBaselineRef = useRef<Partial<Record<Period, number | null>>>({});

  const buildBaseline = useCallback((snap: LeaderboardSnapshot) => {
    if (!baselineRef.current[snap.period]) {
      const base = new Map<number, number>();
      snap.entries.forEach(e => base.set(e.userid, e.rank));
      baselineRef.current[snap.period] = base;
    }
    if (!(snap.period in meBaselineRef.current)) {
      meBaselineRef.current[snap.period] = snap.me?.rank ?? null;
    }
  }, []);

  const isFetching = !USE_MOCK && query.isFetching;
  // Data on hand is for a different period than requested ⇒ mid tab-switch.
  const isSwitching =
    !USE_MOCK &&
    query.isFetching &&
    snapshot != null &&
    snapshot.period !== period;

  return useMemo(() => {
    if (!snapshot) {
      return {
        rows: [],
        top: () => [],
        me: null,
        totalPlayers: 0,
        leaderWins: 0,
        isLoading: !USE_MOCK && query.isLoading,
        isError: !USE_MOCK && query.isError,
        isFetching,
        isSwitching,
      };
    }

    buildBaseline(snapshot);
    const baseline = baselineRef.current[snapshot.period]!;
    const now = Date.now();

    // Flash bookkeeping: any row whose wins grew since the previous snapshot.
    const prevWins = prevWinsRef.current[snapshot.period];
    const nextWins = new Map<number, number>();
    let anyFlash = false;
    snapshot.entries.forEach(e => {
      nextWins.set(e.userid, e.wins);
      if (prevWins && (prevWins.get(e.userid) ?? e.wins) < e.wins) {
        flashRef.current.set(e.userid, now);
        anyFlash = true;
      }
    });
    prevWinsRef.current[snapshot.period] = nextWins;
    if (anyFlash) {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
      }
      flashTimerRef.current = setTimeout(
        () => setFlashTick(n => n + 1),
        FLASH_MS + 50,
      );
    }

    const leaderWins = snapshot.leaderWins || 1;
    const rows: LeaderboardRowVM[] = snapshot.entries.map(e => {
      const flashTs = flashRef.current.get(e.userid);
      // Prefer the server's since-yesterday movement (live mode); fall back to a
      // session-relative baseline when the source omits it (mock / offline).
      let trend: 'up' | 'down' | 'flat';
      let trendAbs: number | null;
      if (e.trend) {
        trend = e.trend;
        trendAbs = e.trendDelta ?? null;
      } else {
        const baseRank = baseline.get(e.userid) ?? e.rank;
        const delta = baseRank - e.rank; // + = climbed since session start
        trend = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
        trendAbs = delta !== 0 ? Math.abs(delta) : null;
      }
      return {
        ...e,
        initials: initialsForName(e.name),
        color: e.you ? lbColors.teal : avatarColorForUser(e.userid),
        medal: MEDAL[e.rank] || null,
        barPct: Math.max(4, Math.round((e.wins / leaderWins) * 100)),
        flash: !!flashTs && now - flashTs < FLASH_MS,
        trend,
        trendAbs,
      };
    });

    const meEntry = snapshot.me;
    let me: MeVM | null = null;
    if (meEntry) {
      // Server since-yesterday movement when present; else session baseline.
      let meTrend: 'up' | 'down' | 'flat';
      let meTrendAbs: number | null;
      if (meEntry.trend) {
        meTrend = meEntry.trend;
        meTrendAbs = meEntry.trendDelta ?? null;
      } else {
        const meBase = meBaselineRef.current[snapshot.period] ?? meEntry.rank;
        const delta =
          meEntry.rank != null && meBase != null ? meBase - meEntry.rank : 0;
        meTrend = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
        meTrendAbs = delta !== 0 ? Math.abs(delta) : null;
      }
      const nextUp =
        meEntry.rank != null
          ? rows.find(r => r.rank === (meEntry.rank as number) - 1)
          : undefined;
      const toNext = nextUp ? Math.max(1, nextUp.wins - meEntry.wins + 1) : 0;
      const lastOfTop = rows[Math.min(TOP_N, rows.length) - 1];
      const winsToEnterTop =
        meEntry.rank != null && meEntry.rank <= TOP_N
          ? 0
          : Math.max(1, (lastOfTop?.wins ?? 0) - meEntry.wins + 1);
      me = {
        ...meEntry,
        initials: initialsForName(meEntry.name),
        trend: meTrend,
        trendAbs: meTrendAbs,
        toNext,
        nextLabel: toNext === 1 ? '1 more win' : `${toNext} more wins`,
        winsToEnterTop,
      };
    }

    return {
      rows,
      top: (limit: number) => rows.slice(0, limit),
      me,
      totalPlayers: snapshot.totalPlayers,
      leaderWins: snapshot.leaderWins,
      isLoading: false,
      isError: false,
      isFetching,
      isSwitching,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    snapshot,
    flashTick,
    buildBaseline,
    query.isLoading,
    query.isError,
    isFetching,
    isSwitching,
  ]);
}
